import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '@/lib/auth';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import {
  useLearningProfile,
  useUpdateProfile,
} from '@/hooks/use-learning-profile';
import type { FeedbackTone, UpdateProfilePayload } from '@/types/learning-profile';
import { useLawSchools, useUpdateProfileSchool } from '@/hooks/use-community';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, X } from 'lucide-react';

interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
}

export function UserProfileForm() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData>({
    firstName: '',
    lastName: '',
    email: user?.email || ''
  });
  // Snapshot of saved values for dirty-tracking
  const [savedData, setSavedData] = useState<{ firstName: string; lastName: string; schoolId: string | null }>({
    firstName: '', lastName: '', schoolId: null,
  });

  // School hooks
  const { data: lawSchools = [] } = useLawSchools();
  const updateSchool = useUpdateProfileSchool();
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(null);
  const [schoolOpen, setSchoolOpen] = useState(false);
  const [schoolSearch, setSchoolSearch] = useState('');
  const schoolInputRef = useRef<HTMLInputElement>(null);

  const filteredSchools = useMemo(() => {
    if (!schoolSearch) return lawSchools.slice(0, 50); // Show first 50 by default
    const q = schoolSearch.toLowerCase();
    return lawSchools.filter(s =>
      s.name.toLowerCase().includes(q) ||
      s.city.toLowerCase().includes(q) ||
      s.state.toLowerCase().includes(q)
    );
  }, [lawSchools, schoolSearch]);

  // Learning profile hooks
  const { data: learningProfile } = useLearningProfile();
  const updateLearningProfile = useUpdateProfile();

  const handleUpdateLearningProfile = async <K extends keyof UpdateProfilePayload>(
    field: K,
    value: UpdateProfilePayload[K]
  ) => {
    try {
      await updateLearningProfile.mutateAsync({ [field]: value } as UpdateProfilePayload);
      toast({
        title: 'Preference updated',
        description: 'Your settings have been saved.',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update preference. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Fetch user profile data
  useEffect(() => {
    async function fetchProfile() {
      if (!user) return;
      
      setLoading(true);
      try {
        // Check if user exists in public.profiles table
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        // If there's a "not found" error, we need to create the profile record
        if (profileError && (profileError.code === 'PGRST116' || profileError.message.includes('not found'))) {
          console.log('Profile record not found, creating new record');
          
          // Insert a new profile record with the current user's ID
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: user.id,
              first_name: '',
              last_name: ''
            });
            
          if (insertError) {
            console.error('Error creating profile record:', insertError);
            toast({
              title: 'Error',
              description: 'Failed to create user profile. Please try again later.',
              variant: 'destructive'
            });
          } else {
            // Successfully created profile record, set empty profile
            setProfileData({
              firstName: '',
              lastName: '',
              email: user.email
            });
          }
        } else if (profileError) {
          // Some other error occurred
          console.error('Error fetching profile:', profileError);
          toast({
            title: 'Error',
            description: 'Failed to load profile data. Please try again later.',
            variant: 'destructive'
          });
        } else if (profileData) {
          // Successfully loaded user profile
          const fn = profileData.first_name || '';
          const ln = profileData.last_name || '';
          const sid = (profileData as any).school_id || null;
          setProfileData({ firstName: fn, lastName: ln, email: user.email });
          setSelectedSchoolId(sid);
          setSavedData({ firstName: fn, lastName: ln, schoolId: sid });
        }
      } catch (error) {
        console.error('Exception fetching profile:', error);
        toast({
          title: 'Error',
          description: 'An unexpected error occurred. Please try again later.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    }
    
    fetchProfile();
  }, [user, toast]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    setSaving(true);
    try {
      // First update the profile in the database
      const { error } = await supabase.rpc('update_user_profile', {
        user_id: user.id,
        new_first_name: profileData.firstName,
        new_last_name: profileData.lastName
      });
      
      if (error) {
        throw error;
      }

      // Save school to profiles table
      if (selectedSchoolId !== null) {
        await updateSchool.mutateAsync(selectedSchoolId);
      }

      // Invalidate sidebar profile name cache so it updates immediately
      queryClient.invalidateQueries({ queryKey: ['profile-name'] });

      // Update saved snapshot so button hides
      setSavedData({ firstName: profileData.firstName, lastName: profileData.lastName, schoolId: selectedSchoolId });

      toast({
        title: 'Success',
        description: 'Profile updated successfully',
        variant: 'default'
      });
    } catch (error: unknown) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update profile',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Dirty check — button only shows when something changed
  const isDirty =
    profileData.firstName !== savedData.firstName ||
    profileData.lastName !== savedData.lastName ||
    selectedSchoolId !== savedData.schoolId;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center p-4">
        <LoadingSpinner className="h-6 w-6" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="firstName" className="text-gray-700 dark:text-gray-300">First Name</Label>
          <Input
            id="firstName"
            name="firstName"
            value={profileData.firstName}
            onChange={handleChange}
            placeholder="First Name"
            className="border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-orange-500 dark:focus:ring-orange-400"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="lastName" className="text-gray-700 dark:text-gray-300">Last Name</Label>
          <Input
            id="lastName"
            name="lastName"
            value={profileData.lastName}
            onChange={handleChange}
            placeholder="Last Name"
            className="border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-orange-500 dark:focus:ring-orange-400"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email" className="text-gray-700 dark:text-gray-300">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={profileData.email}
          readOnly={true}
          disabled={true}
          className="opacity-75 cursor-not-allowed border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400"
          placeholder="Email"
        />
      </div>

      {/* School Selection — searchable dropdown */}
      <div className="space-y-2">
        <Label className="text-gray-700 dark:text-gray-300">Law School</Label>
        <div className="relative">
          <button
            type="button"
            role="combobox"
            aria-expanded={schoolOpen}
            onClick={() => {
              setSchoolOpen(!schoolOpen);
              if (!schoolOpen) setTimeout(() => schoolInputRef.current?.focus(), 0);
            }}
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border px-3 py-2 text-sm',
              'border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white',
              'focus:outline-none focus:ring-2 focus:ring-orange-500'
            )}
          >
            <span className={cn('truncate', !selectedSchoolId && 'text-muted-foreground')}>
              {selectedSchoolId
                ? lawSchools.find(s => s.id === selectedSchoolId)?.name || 'Loading...'
                : 'Search law schools...'}
            </span>
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedSchoolId && (
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => { e.stopPropagation(); setSelectedSchoolId(null); }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              )}
              <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
            </div>
          </button>
          {schoolOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => { setSchoolOpen(false); setSchoolSearch(''); }} />
              <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg">
                <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                  <input
                    ref={schoolInputRef}
                    type="text"
                    value={schoolSearch}
                    onChange={(e) => setSchoolSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') { setSchoolOpen(false); setSchoolSearch(''); } }}
                    placeholder="Type to search..."
                    className="w-full text-sm bg-transparent outline-none placeholder:text-muted-foreground dark:text-white"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto p-1">
                  {filteredSchools.map(school => (
                    <div
                      key={school.id}
                      role="option"
                      aria-selected={selectedSchoolId === school.id}
                      onClick={() => { setSelectedSchoolId(school.id); setSchoolOpen(false); setSchoolSearch(''); }}
                      className={cn(
                        'flex items-center gap-2 rounded px-2 py-1.5 text-sm cursor-pointer hover:bg-accent',
                        selectedSchoolId === school.id && 'bg-accent'
                      )}
                    >
                      <Check className={cn('h-4 w-4 shrink-0', selectedSchoolId === school.id ? 'opacity-100' : 'opacity-0')} />
                      <div className="min-w-0">
                        <div className="truncate font-medium dark:text-white">{school.name}</div>
                        <div className="text-xs text-muted-foreground">{school.city}, {school.state}</div>
                      </div>
                    </div>
                  ))}
                  {filteredSchools.length === 0 && (
                    <p className="py-4 text-center text-sm text-muted-foreground">No school found.</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Learning Preferences - Side by Side */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="feedback-tone" className="text-gray-700 dark:text-gray-300">Feedback style</Label>
          <Select
            value={learningProfile?.feedback_tone || 'balanced'}
            onValueChange={(value) => handleUpdateLearningProfile('feedback_tone', value as FeedbackTone)}
            disabled={updateLearningProfile.isPending}
          >
            <SelectTrigger id="feedback-tone" className="border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
              <SelectValue placeholder="Select feedback style" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="balanced">Balanced</SelectItem>
              <SelectItem value="direct">Direct</SelectItem>
              <SelectItem value="encouraging">Encouraging</SelectItem>
              <SelectItem value="strict">Strict</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="session-length" className="text-gray-700 dark:text-gray-300">Typical session length</Label>
          <Select
            value={String(learningProfile?.avg_session_length_minutes || 20)}
            onValueChange={(value) => handleUpdateLearningProfile('avg_session_length_minutes', parseInt(value, 10))}
            disabled={updateLearningProfile.isPending}
          >
            <SelectTrigger id="session-length" className="border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
              <SelectValue placeholder="Select session length" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">Quick (5-10 min)</SelectItem>
              <SelectItem value="20">Medium (15-20 min)</SelectItem>
              <SelectItem value="30">Long (30+ min)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {(isDirty || saving) && (
        <Button
          type="submit"
          disabled={saving}
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          {saving ? (
            <>
              <LoadingSpinner className="mr-2 h-4 w-4" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      )}
    </form>
  );
} 